// Pure, import-free tag-text helper (no @/store / Vue) so it is unit-testable
// standalone under plain node. `clearText` in tools.js delegates here — this is the
// "Check Text" normalization for the Additional Tags field.

const REGEX_SPACE = / /g;
const REGEX_POUNT = /\./g;

// Comparison key: spaces + dots removed, lowercased. Used ONLY to detect
// duplicates — never for output (output preserves the original case).
function normKey(tag) {
    return tag.replace(REGEX_SPACE, '').replace(REGEX_POUNT, '').toLowerCase();
}

// Two tags are duplicates when their keys are equal OR one is the other + a
// trailing "s" (singular<->plural, e.g. "knuckle"/"knuckles"). Deliberately only
// the +"s" relation — an unconditional trailing-s strip would over-merge.
function isDuplicateKey(a, b) {
    return a === b || a === `${b}s` || b === `${a}s`;
}

/**
 * De-duplicate a comma-separated tag string.
 * - dedups case/space/punct-insensitively AND across singular<->plural
 * - on a case-insensitive duplicate, KEEPS the variant that starts with an
 *   uppercase letter (so "Egypt" beats "egypt")
 * - preserves the original case of the kept tag (never lowercases output)
 * - strips a trailing "." and skips empty entries
 */
export function dedupeTags(text) {
    const parts = String(text ?? '').split(',');
    const kept = []; // { display, key }

    for (const raw of parts) {
        let display = raw.trim();
        if (display === '') continue;
        if (display[display.length - 1] === '.') display = display.slice(0, -1);
        if (display === '') continue;

        const key = normKey(display);
        const dupIndex = kept.findIndex((k) => isDuplicateKey(key, k.key));

        if (dupIndex === -1) {
            kept.push({ display, key });
            continue;
        }
        // Duplicate: prefer the capitalized variant, keep the rest as-is.
        const existing = kept[dupIndex];
        const incomingCap = /^[A-Z]/.test(display);
        const existingCap = /^[A-Z]/.test(existing.display);
        if (incomingCap && !existingCap) {
            kept[dupIndex] = { display, key: existing.key };
        }
    }

    return kept.map((k) => k.display).join(', ');
}
